import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Schedule
} from '@mui/icons-material';
import { useApp } from '../../../context/AppContext';
import TimeSlotModal from './TimeSlotModal';
import api from '../../../utils/api';

const TimeSlots = () => {
  const { actions } = useApp();
  const [timeSlots, setTimeSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [slotToDelete, setSlotToDelete] = useState(null);

  useEffect(() => {
    fetchTimeSlots();
  }, []);

  const fetchTimeSlots = async () => {
    try {
      setLoading(true);
      const response = await api.get('/time-slots');
      setTimeSlots(response.data.timeSlots || []);
    } catch (error) {
      console.error('Error fetching time slots:', error);
      actions.showSnackbar('Failed to fetch time slots', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleAddSlot = () => {
    setSelectedSlot(null);
    setModalOpen(true);
  };

  const handleEditSlot = (slot) => {
    setSelectedSlot(slot);
    setModalOpen(true);
  };

  const handleDeleteSlot = (slot) => {
    setSlotToDelete(slot);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/time-slots/${slotToDelete.id}`);
      actions.showSnackbar('Time slot deleted successfully', 'success');
      fetchTimeSlots();
    } catch (error) {
      console.error('Error deleting time slot:', error);
      actions.showSnackbar('Failed to delete time slot', 'error');
    } finally {
      setDeleteDialogOpen(false);
      setSlotToDelete(null);
    }
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedSlot(null);
  };

  const handleSlotSaved = () => {
    fetchTimeSlots();
    handleModalClose();
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const getBranchNames = (branches) => {
    if (!branches || branches.length === 0) return 'No branches';
    return branches.map(branch => branch.name || branch.branchName || 'Unknown').join(', ');
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Schedule sx={{ fontSize: 28, color: 'primary.main' }} />
          <Typography variant="h4" component="h1" fontWeight="bold">
            Delivery Time Slots
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleAddSlot}
          sx={{ borderRadius: 2 }}
        >
          Add Time Slot
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
          {timeSlots.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Schedule sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No Time Slots Found
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Create your first delivery time slot to get started.
              </Typography>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={handleAddSlot}
              >
                Add Time Slot
              </Button>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead sx={{ bgcolor: 'primary.main' }}>
                  <TableRow>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Time Range</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Applicable Branches</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Status</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {timeSlots.map((slot, index) => (
                    <TableRow key={slot.id || index} sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
                      <TableCell>
                        <Typography variant="body1" fontWeight="medium">
                          {formatTime(slot.startTime)} - {formatTime(slot.endTime)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {getBranchNames(slot.branches)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={slot.active !== false ? 'Active' : 'Inactive'}
                          color={slot.active !== false ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <IconButton
                            size="small"
                            onClick={() => handleEditSlot(slot)}
                            color="primary"
                          >
                            <Edit />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteSlot(slot)}
                            color="error"
                          >
                            <Delete />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}

      {/* Time Slot Modal */}
      <TimeSlotModal
        open={modalOpen}
        onClose={handleModalClose}
        onSave={handleSlotSaved}
        slot={selectedSlot}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete Time Slot</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Are you sure you want to delete this time slot?
          </Alert>
          {slotToDelete && (
            <Typography variant="body1">
              <strong>Time:</strong> {formatTime(slotToDelete.startTime)} - {formatTime(slotToDelete.endTime)}
              <br />
              <strong>Branches:</strong> {getBranchNames(slotToDelete.branches)}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TimeSlots; 